import type { PostPublic, PostMutationOptions } from '@repo/types/posts';

import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { type InfiniteData, type UseInfiniteQueryResult } from '@tanstack/react-query';

import EmptyPostsList from '@/components/features/post/EmptyPostList';
import PostCard from '@/components/features/post/PostCard';
import PostCardSkeleton from '@/components/features/post/PostCardSkeleton';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';

interface PostListProps {
  query: UseInfiniteQueryResult<InfiniteData<PostPublic[], unknown>, Error>;
  options?: PostMutationOptions;
}

export default function PostList(postListProps: PostListProps) {
  const { query, options } = postListProps;

  const { data, isSuccess, isFetching, isLoading, hasNextPage, isError, fetchNextPage } = query;

  const { data: posts, isEmpty: postsIsEmpty } = useInfiniteQueryResult(data);

  const next = async () => {
    await fetchNextPage();
  };

  return (
    <div className={'flex w-full flex-col gap-4'}>
      {posts.map((post) => (
        <PostCard key={post.id} post={post} options={options} />
      ))}
      {isSuccess && postsIsEmpty && <EmptyPostsList />}
      {isLoading && (
        <>
          <PostCardSkeleton />
          <PostCardSkeleton />
          <PostCardSkeleton />
        </>
      )}
      <InfiniteScroll hasNextPage={hasNextPage} isLoading={isLoading} isFetching={isFetching} next={next} threshold={0.25} root={null}>
        {hasNextPage && !isError && <PostCardSkeleton />}
      </InfiniteScroll>
    </div>
  );
}
